import { PRIVILEGES } from 'constants/privileges';
import { useEffect, useMemo, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Chip, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Box } from '@mui/system';
import { InvoiceService } from 'api';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { LoadingScreen } from 'components/Loaders/LoadingScreen';
import { useAuth } from 'contexts/authContext';
import { CorrectiveInvoice } from 'pages/invoices/invoices.types';
import { useTranslation } from 'react-i18next';
import { MdEdit } from 'react-icons/md';
import { useParams } from 'react-router-dom';
import { InvoiceTab, InvoiceType } from 'root/pathnames';
import { hasPrivilege } from 'utils/authFunctions.utils';
import { epochToISO } from 'utils/dateFunctions.utils';
import { canEditInvoice } from 'utils/invoiceFunctions.utils';
import { InvoiceForm } from '../InvoiceForm';
import { InvoicesTabs } from '../InvoicesTabs';
import { InvoiceAllocations } from './components/InvoiceAllocations';
import { InvoiceLinesPanel } from './components/InvoiceLinesPanel';
import { SideTable } from './components/SideTable';
import * as S from './details.styled';
import { isAllocationTabVisible } from './InvoiceDetails.utils';

export const InvoiceDetails = () => {
  const { user, allowedReplacementsByCode } = useAuth();

  const {
    tab: invoiceTab,
    invoiceType,
    invoiceId,
  } = useParams<{
    tab: string;
    invoiceType: InvoiceType;
    invoiceId: string;
  }>();

  const { t } = useTranslation([
    'misc',
    'invoice',
    'counterparty',
    'currency',
    'clause',
  ]);

  const [activeTab, setActiveTab] = useState('invoice');
  const [isEditing, setIsEditing] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(true);

  const isCorrective = invoiceType === InvoiceType.CORRECTIVE_INVOICE;

  const {
    loading: invoiceLoading,
    getInvoiceById,
    data: invoice,
  } = InvoiceService.useGetInvoiceById();

  const canBeEdited = useMemo(
    () =>
      invoice &&
      !invoice.publicationDate &&
      !invoice.deleted &&
      !isEditing &&
      canEditInvoice(invoice, user, allowedReplacementsByCode),
    [invoice, isEditing, user, allowedReplacementsByCode],
  );

  useEffect(() => {
    getInvoiceById(Number(invoiceId), isCorrective);
    setIsEditing(false);
  }, [invoiceId, getInvoiceById, isCorrective]);

  const isForeign = useMemo(
    () => invoiceTab === InvoiceTab.FOREIGN,
    [invoiceTab],
  );

  const handleChangeTab = (_: React.SyntheticEvent, selectedTab: string) => {
    setActiveTab(selectedTab);
  };

  const handleStartEditing = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    if (!isDetailsOpen) setIsDetailsOpen(true);
    setIsEditing((prev) => !prev);
  };

  const getSaldeoDocumentUrl = (saldeoDocumentId: string | number) =>
    `https://saldeo.brainshare.pl/biuro/dokumenty/detale-dokumentu.hta?id=${encodeURIComponent(
      saldeoDocumentId,
    )}&section=DOCUMENT_DETAILS`;

  return invoice &&
    hasPrivilege(PRIVILEGES.Invoices.VIEW_INVOICE_DETAILS, user) ? (
    <Box sx={{ display: 'flex' }}>
      <S.InvoiceDetailsContainer>
        <S.InvoiceTabsContainer>
          <InvoicesTabs />
        </S.InvoiceTabsContainer>
        <SideTable
          onInvoiceChange={() => setActiveTab('invoice')}
          invoice={invoice}
        />
        <S.InvoiceDetailsWrapper>
          <LoaderFullScreen loading={invoiceLoading} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <S.Title>
              {t('invoice', { ns: 'invoice' })} {invoice.code}
            </S.Title>
            {invoice.saldeoDocumentId && (
              <Tooltip title={t('seeInSaldeo', { ns: 'invoice' })}>
                <IconButton
                  size='small'
                  sx={{ mt: 1.2 }}
                  color='primary'
                  aria-label={t('seeInSaldeo', { ns: 'invoice' })}
                  onClick={() =>
                    window.open(
                      getSaldeoDocumentUrl(invoice.saldeoDocumentId!),
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                >
                  <OpenInNewIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              marginBottom: '0.025rem',
            }}
          >
            <Tabs value={activeTab} onChange={handleChangeTab}>
              <Tab label={t('invoice', { ns: 'invoice' })} value={'invoice'} />
              {isAllocationTabVisible(invoice) && (
                <Tab
                  label={t('allocations', { ns: 'invoice' })}
                  value={'allocations'}
                />
              )}
            </Tabs>
          </Box>
          {activeTab === 'invoice' ? (
            <>
              <S.StyledAccordion defaultExpanded expanded={isDetailsOpen}>
                <S.IconLeftAccordionSummary
                  expandIcon={<ExpandMoreIcon fontSize='large' />}
                  onClick={() => setIsDetailsOpen((p) => !p)}
                >
                  <S.Subtitle>{t('data', { ns: 'misc' })}</S.Subtitle>
                  {canBeEdited && (
                    <IconButton
                      size='small'
                      color='inherit'
                      onClick={handleStartEditing}
                    >
                      <MdEdit size={20} />
                    </IconButton>
                  )}
                </S.IconLeftAccordionSummary>
                <AccordionDetails>
                  {isEditing ? (
                    <InvoiceForm
                      isEditing
                      invoiceData={invoice}
                      cancelEditing={() => setIsEditing(false)}
                      refetchInvoiceData={getInvoiceById}
                    />
                  ) : (
                    <S.List>
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('code', { ns: 'invoice' })}
                        </S.ListRowTitle>
                        <S.ListRowData>{invoice.code}</S.ListRowData>
                      </S.ListRow>
                      {isCorrective && (
                        <S.ListRow>
                          <S.ListRowTitle>
                            {t('forInvoice', { ns: 'invoice' })}
                          </S.ListRowTitle>
                          <S.ListRowData>
                            {
                              (invoice as CorrectiveInvoice)
                                .correctedInvoiceCode
                            }
                          </S.ListRowData>
                        </S.ListRow>
                      )}
                      {isForeign && (
                        <S.ListRow>
                          <S.ListRowTitle>
                            {t('logNumber', { ns: 'invoice' })}
                          </S.ListRowTitle>
                          <S.ListRowData>{invoice.logNumber}</S.ListRowData>
                        </S.ListRow>
                      )}
                      {
                        <S.ListRow>
                          <S.ListRowTitle>
                            {t('counterparty', { ns: 'counterparty' })}
                          </S.ListRowTitle>
                          <S.ListRowData>
                            {isForeign
                              ? invoice.sellerCounterparty.code
                              : invoice.buyerCounterparty.code}
                          </S.ListRowData>
                        </S.ListRow>
                      }
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('currency', { ns: 'currency' })}
                        </S.ListRowTitle>
                        <S.ListRowData>
                          {invoice.currencyCode.currency}
                        </S.ListRowData>
                      </S.ListRow>
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('issueDate', { ns: 'invoice' })}
                        </S.ListRowTitle>
                        <S.ListRowData>
                          {epochToISO(invoice.issueDate)}
                        </S.ListRowData>
                      </S.ListRow>
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('saleDate', { ns: 'invoice' })}
                        </S.ListRowTitle>
                        <S.ListRowData>
                          {epochToISO(invoice.saleDate)}
                        </S.ListRowData>
                      </S.ListRow>
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('dueDate', { ns: 'invoice' })}
                        </S.ListRowTitle>
                        <S.ListRowData>
                          {epochToISO(invoice.dueDate)}
                        </S.ListRowData>
                      </S.ListRow>
                      {isForeign ? (
                        <S.ListRow>
                          <S.ListRowTitle>
                            {t('receiptDate', { ns: 'invoice' })}
                          </S.ListRowTitle>
                          <S.ListRowData>
                            {epochToISO(invoice.receiptDate)}
                          </S.ListRowData>
                        </S.ListRow>
                      ) : (
                        <S.ListRow>
                          <S.ListRowTitle>
                            {t('exchangeRateDate', { ns: 'invoice' })}
                          </S.ListRowTitle>
                          <S.ListRowData>
                            {epochToISO(invoice.exchangeRateDate)}
                          </S.ListRowData>
                        </S.ListRow>
                      )}
                      {!isForeign && (
                        <S.ListRow align={isEditing ? 'center' : 'top'}>
                          <S.ListRowTitle>
                            {t('body', { ns: 'clause' })}
                          </S.ListRowTitle>
                          <S.ListRowData>
                            {invoice.clauses &&
                              invoice.clauses.map(
                                (clause, i) =>
                                  !clause.mandatory && (
                                    <Box paddingBottom='5px' key={i}>
                                      <Chip
                                        label={clause.message}
                                        size='small'
                                        variant='filled'
                                      />
                                    </Box>
                                  ),
                              )}
                          </S.ListRowData>
                        </S.ListRow>
                      )}
                      <S.DetailsDivider variant='middle' light />
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('description', { ns: 'misc' })}
                        </S.ListRowTitle>
                        <S.ListRowData>{invoice.description}</S.ListRowData>
                      </S.ListRow>
                      <S.ListRow>
                        <S.ListRowTitle>
                          {t('comment', { ns: 'misc' })}
                        </S.ListRowTitle>
                        <S.ListRowData>{invoice.comment}</S.ListRowData>
                      </S.ListRow>
                    </S.List>
                  )}
                </AccordionDetails>
              </S.StyledAccordion>
              <InvoiceLinesPanel
                invoice={invoice}
                refetchInvoiceData={getInvoiceById}
              />
            </>
          ) : (
            <InvoiceAllocations
              invoice={invoice}
              refetchInvoiceData={getInvoiceById}
            />
          )}
        </S.InvoiceDetailsWrapper>
      </S.InvoiceDetailsContainer>
    </Box>
  ) : invoiceLoading ? (
    <LoadingScreen />
  ) : null;
};
